import { useEffect, useState, useCallback } from "react";

interface UseExitIntentOptions {
  threshold?: number;
  delayMs?: number;
  sessionKey?: string;
  disableOnMobile?: boolean;
  disabled?: boolean;
}

export const useExitIntent = (options: UseExitIntentOptions = {}) => {
  const {
    threshold = 20,
    delayMs = 500,
    sessionKey = "exit_intent_shown",
    disableOnMobile = true,
    disabled = false,
  } = options;

  const [showPopup, setShowPopup] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  const closePopup = useCallback(() => {
    setShowPopup(false);
    // Store in sessionStorage so it doesn't show again this session
    try {
      sessionStorage.setItem(sessionKey, "true");
    } catch (e) {
      // Handle private browsing or quota exceeded
    }
  }, [sessionKey]);

  useEffect(() => {
    // Skip if disabled
    if (disabled) {
      return;
    }

    // Check if already shown this session
    try {
      if (sessionStorage.getItem(sessionKey) === "true") {
        setHasShown(true);
        return;
      }
    } catch (e) {
      // Handle private browsing
    }

    // Disable on mobile if specified
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    if (disableOnMobile && isMobile) {
      return;
    }

    let timeoutId: NodeJS.Timeout;

    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger when mouse moves toward top of viewport
      if (e.clientY <= threshold && !hasShown) {
        timeoutId = setTimeout(() => {
          setShowPopup(true);
          setHasShown(true);
        }, delayMs);
      }
    };

    const handleMouseEnter = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("mouseenter", handleMouseEnter);

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("mouseenter", handleMouseEnter);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [threshold, delayMs, hasShown, sessionKey, disableOnMobile, disabled]);

  return { showPopup, closePopup, hasShown };
};
